/**
 * Reactive store. Replaces the bag of top-level `let` globals
 * (allColumns, selectedCols, filterLogic, …) the old single-file UI used
 * (the shared reactive state model).
 *
 * Components subscribe in `connectedCallback` via `StoreController`. Domain
 * types live in `api/types.ts`. This file is a SEED: it holds the core atoms
 * needed early. Add atoms as each §10 area is ported - do not pre-build state
 * for features that aren't migrated yet.
 */

import type {
  AccessExplanation,
  ColumnInfo,
  EstimateResponse,
  ExportFormat,
  FillMethod,
  FilterCondition,
  FleetSummary,
  ShipDateRange,
  SfocCurves,
  SPCorrections,
  SPDistFeature,
  SPExplain,
  SPSchema,
  SPSweep,
  SPTimeseries,
} from '../api/types.ts';
import { Atom } from './atom.ts';
import { presetWindow } from '../utils/dates.ts';

// `Atom` was defined here until feature flags needed one of their own, and
// `store.ts` asks `feature-flags.ts` about `live` while building its atoms -
// two modules that construct each other's values at import time. It moved to
// `atom.ts` and is re-exported, so every `import { Atom } from './store.ts'`
// still reads correctly.
export { Atom } from './atom.ts';

/**
 * Top-level section. Explore is every existing tab; Dashboards is the tiled
 * multi-chart view; Assistant is the conversation, which is also reachable as a
 * drawer over any other section (see `docs/plans/assistant.md`).
 *
 * A section rather than a fifth tab because the tabs all share the `.two-panel`
 * chrome - query sidebar plus right panel - and a dashboard wants the full
 * width. The switch works exactly like the tab switch: an atom, a
 * `document.body.dataset` attribute for the CSS to key off, and a clean path
 * produced by `state/app-route.ts`.
 */
export type Section =
  | 'home'
  | 'explore'
  | 'dashboards'
  | 'catalog'
  | 'assistant'
  | 'argus'
  | 'live-status';

export type Tab = 'export' | 'plot' | 'modelviewer';
/** Sub-tabs within the Model Viewer tab. Extend as more model views are added. */
export type ModelTab = 'sp' | 'sfoc';
export type AssistantTab = 'chat' | 'history';
/** A data source id from `/api/sources` (`state/data-sources.ts`). */
export type DataSource = string;
export type DatePreset =
  | '7d' | '30d' | '1mo' | '3m' | '6m' | '1y' | 'max' | 'custom'
  | 'today' | 'yesterday' | 'thisweek' | 'lastweek'
  | 'mtd' | 'prevmonth' | 'ytd' | 'prevyear'
  | '5m' | '10m' | '30m' | '1h' | '6h' | '24h'
  | '15m' | '2h' | '3h' | '12h' | '48h';

/** Read a persisted value from localStorage, falling back to a default. */
function persisted<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  const v = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  return v !== null && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

/**
 * The tab the app opens on. Plot is the landing view: it is what most sessions
 * start from, and Export is one click away.
 *
 * Kept in step with `appendTab`/`restoreFromUrl` in `url-sync.ts`, which omit
 * whichever tab is the default from a shared link.
 */
export const activeTab = new Atom<Tab>('plot');

/** Active top-level section. */
export const activeSection = new Atom<Section>('explore');
/** Active sub-tab within the full-width Assistant section. */
export const assistantTab = new Atom<AssistantTab>('chat');
/**
 * The data source Explore reads. Every session starts on EOS; a link may name
 * another (`ds=`). Not restored from the last session: a source that is down
 * now would open Explore on a step that cannot load.
 */
export const activeDataSource = new Atom<DataSource>('historical');

/** Active sub-tab within the Model Viewer (Service Power vs SFOC). */
export const activeModelTab = new Atom<ModelTab>('sp');

/** Ship-list loading state (drives the spinner / disabled select / error). */
export const shipsLoading = new Atom<boolean>(false);
export const shipLoadError = new Atom<string | null>(null);

/**
 * Whether this deployment has the metadata database behind it, from
 * `/api/info`. False is a supported state and not an error - the app is then
 * the ad hoc one it was before the catalog existed - so features made of stored
 * state read this to say they are unavailable rather than letting a 503 through.
 */
export const catalogAvailable = new Atom<boolean>(false);

/**
 * Whether `catalogAvailable` is an answer yet, rather than its default. Until
 * `/api/info` returns, "false" means "not asked", and something that renders
 * straight away - a linked dashboard tile - would otherwise tell people the
 * catalog is gone for the half second before it says it is loading.
 */
export const catalogAvailabilityKnown = new Atom<boolean>(false);

/**
 * What the server says this account may do, from `GET /api/me`, plus whether it
 * is root. Empty until that call returns, and empty forever without a database.
 *
 * Used only to decide what to *offer* - every one of these is checked again on
 * the server, which is the check that counts. Hiding a control the server would
 * refuse is a courtesy; it is not the gate.
 */
export const myPermissions = new Atom<ReadonlySet<string>>(new Set());
export const isRootUser = new Atom<boolean>(false);
/** The database's id for this account, or null without a database. */
export const myUserId = new Atom<string | null>(null);

/**
 * Every capability decision for this account, with its reason, from the
 * server's access evaluator (`/api/me` `access`). Null without a database, or
 * before the first answer. Roles give permissions; Features can still block
 * them - this is the combined answer, and the server enforces the same one.
 */
export const myAccess = new Atom<AccessExplanation | null>(null);

/** Whether a capability (`argus.triage`, `assistant.use` ...) is allowed. Null: unknown. */
export function capabilityAllowed(key: string): boolean | null {
  const found = myAccess.value?.capabilities.find((c) => c.key === key);
  return found ? found.allowed : null;
}

/** Whether this account holds a permission. Root holds everything implicitly. */
export function canDo(permission: string): boolean {
  return isRootUser.value || myPermissions.value.has(permission);
}

export const selectedShip = new Atom<string | null>(null);
export const ships = new Atom<string[]>([]);

/**
 * Extra ships plotted alongside `selectedShip` on the Plot tab (multi-ship
 * comparison). `selectedShip` stays the anchor everywhere else - it drives
 * `allColumns`, `shipDateRange`, filter pruning and the whole Export tab - so
 * comparison ships are additive and never rewrite that single-ship path.
 *
 * Order is selection order, which keeps each ship's colour stable as more are
 * added.
 */
export const comparisonShips = new Atom<string[]>([]);

/**
 * Display metadata per ship code, from `/api/ships/names`. Read through the
 * helpers in `utils/ships.ts` rather than directly - they handle the fallback
 * for a ship with no mapping.
 */
export const shipMeta = new Atom<Record<string, { name: string; brand: string }>>({});

/** Hard cap on ships plotted at once, anchor included. */
export const MAX_PLOT_SHIPS = 6;

/**
 * Every ship the Plot tab should draw, anchor first. Empty when no ship is
 * selected at all.
 */
export function plottedShipIds(): string[] {
  const anchor = selectedShip.value;
  if (!anchor) return [];
  const extras = comparisonShips.value.filter((s) => s && s !== anchor);
  return [anchor, ...extras].slice(0, MAX_PLOT_SHIPS);
}

/** True when the Plot tab is drawing more than one ship. */
export function isMultiShip(): boolean {
  return plottedShipIds().length > 1;
}
export const allColumns = new Atom<ColumnInfo[]>([]);
export const selectedCols = new Atom<Set<string>>(new Set());
export const shipDateRange = new Atom<ShipDateRange | null>(null);
export const metadataUpdatedAt = new Atom<string | null>(null);
// Fleet-wide counts from /api/info, for Home's stat strip. Null on an older
// backend, and Home then shows what it does know rather than a zero.
export const fleetSummary = new Atom<FleetSummary | null>(null);
// Backend-reported version + git SHA (from /api/info), shown in the header
// tooltip to confirm what's actually deployed. Null until the backend reports it.
export const backendVersion = new Atom<string | null>(null);
export const backendGitSha = new Atom<string | null>(null);
export const startDate = new Atom<string>('2023-01-01T00:00:00');
export const endDate = new Atom<string>('2023-02-01T00:00:00');
export const activePreset = new Atom<DatePreset>('custom');

/** UI-side filter row - value carries an extra `id` so we can re-render stably. */
export interface FilterRow extends FilterCondition {
  id: string;
  /**
   * Set when this row is a **slicer**: a range over `column` applied to the
   * points already drawn, dragged live on the chart, instead of a condition
   * the query applies. `op`/`value` are ignored while it is set. `null` ends
   * are open. Charts the server aggregates (and Export) receive it as an
   * ordinary `>=` / `<=` pair - see `getFilters`.
   */
  slicer?: { min: number | null; max: number | null };
}

export const filters = new Atom<FilterRow[]>([]);
export const filterLogic = new Atom<'AND' | 'OR'>('AND');

/**
 * Experimental free-form filter expression (e.g. `rpm > stw * 2 AND fuel < 100`).
 * `filterExprError` holds the live client-side validation message (null =
 * valid/empty); the backend re-validates authoritatively. Only sent when
 * non-empty and locally valid. It is one row in the Filters list, ANDed with
 * the others - see `filterMode`.
 */
export const filterExpr = new Atom<string>('');
export const filterExprError = new Atom<string | null>(null);

/**
 * Which filter parts are live. The Filters card is one list - rows, slicers and
 * an expression, ANDed together (the backend always ANDed them) - so the app
 * itself only ever uses `'and'`.
 *
 * `'builder'` and `'expr'` are the old either/or switch, kept because saved
 * links and dashboard tiles still carry it (`fmode`): a link restored into
 * Explore is normalised to `'and'` by dropping whichever half used to be
 * inactive, and a tile keeps resolving its own recipe exactly as it was saved.
 */
export type FilterMode = 'builder' | 'expr' | 'and';
export const filterMode = new Atom<FilterMode>('and');

/**
 * Derived signals - user-defined columns computed as
 * `operand_a <op> operand_b`, where each operand is either a raw numeric column
 * (the `*_col`/`operand_a` string) or a constant (the `*_const` number). They
 * appear as plottable pseudo-columns and are usable in the y-axis, color-by, and
 * filters - but are never sent on export. Lives in plot state in the blob; here
 * it's its own atom (the plot-state area, §10.7, hasn't landed yet).
 */
export interface DerivedSignal {
  name: string;
  op: '+' | '-' | '*' | '/';
  operand_a: string | null;
  operand_a_const: number | null;
  operand_b_col: string | null;
  operand_b_const: number | null;
  /** Experimental free-form numeric expression (e.g. `rpm * 2 + stw`). When
   * set and non-blank it supersedes the structured operand/op fields. */
  expr?: string | null;
}

export const derivedSignals = new Atom<DerivedSignal[]>([]);

/**
 * Statistical outlier filter, applied to both export and plot. `method: 'none'`
 * is OFF - the default - and nothing is sent (no `ol=` URL param) until the user
 * picks a method AND at least one target column. Bounds are computed server-side
 * over the in-range filtered data (see backend/outliers.py); `action` chooses
 * between dropping out-of-range rows and blanking just the offending value.
 */
export type OutlierMethod = 'none' | 'iqr' | 'zscore' | 'percentile' | 'mad' | 'hampel';
export interface OutlierState {
  method: OutlierMethod;
  columns: string[];
  action: 'drop' | 'null';
  k: number; // IQR fence multiplier
  nSigma: number; // zscore / mad / hampel multiplier
  pLow: number; // percentile lower bound (%)
  pHigh: number; // percentile upper bound (%)
  window: number; // hampel rolling half-window (samples each side)
}
export const DEFAULT_OUTLIERS: OutlierState = {
  method: 'none',
  columns: [],
  action: 'drop',
  k: 1.5,
  nSigma: 3,
  pLow: 1,
  pHigh: 99,
  window: 7,
};
export const outliers = new Atom<OutlierState>({ ...DEFAULT_OUTLIERS });

export const exportFormat = new Atom<ExportFormat>('csv');
export const fillMethod = new Atom<FillMethod>('none');
export const frequencyValue = new Atom<string>('');
export const frequencyUnit = new Atom<'min' | 'h' | 'd'>('min');

/**
 * Result of the most recent `/estimate`. Written by the
 * debounced scheduler in `state/estimate.ts`; read by the filter match-count /
 * zero-row diagnostic (§10.5) and - once ported - the export stats strip (§10.6).
 * `estimatePending` is true between a scheduled estimate and its resolution.
 */
export const lastEstimate = new Atom<EstimateResponse | null>(null);
export const estimatePending = new Atom<boolean>(false);

/* ─── SP model viewer ──────────────────────────────────────
 * State for the SP model viewer tab. The ship comes from `selectedShip` and the
 * distribution window from `startDate`/`endDate` (shared with the other tabs).
 */
export const spVersions = new Atom<string[]>([]);
export const spVersion = new Atom<string | null>(null);
export const spSchema = new Atom<SPSchema | null>(null);
export const spDistributions = new Atom<SPDistFeature[]>([]);
/** Current feature vector (the slider/datetime-init values) fed to /predict. */
export const spValues = new Atom<Record<string, number>>({});
/** Actual recorded service power at the loaded datetime (null if none/unloaded). */
export const spActual = new Atom<number | null>(null);
/** Latest model prediction for `spValues` (null while incomplete/pending). */
export const spPredicted = new Atom<number | null>(null);
/** Additive SP correction factor (kW) for the loaded timestamp (null if none).
 * Fixed while perturbing sliders; corrected service power = predicted + this. */
export const spCorrection = new Atom<number | null>(null);
/** ISO timestamp at which the in-force correction factor was written (so the
 * viewer can show *which* correction is applied, not just its value). */
export const spCorrectionTimestamp = new Atom<string | null>(null);
/** Datetime (UTC) used to initialise the feature vector from real data. */
export const spInitDatetime = new Atom<string>('');
export const spLoading = new Atom<boolean>(false);
export const spError = new Atom<string | null>(null);
/** Local hour-of-day (0–24) driving the derived hour_sin/hour_cos features. The
 * derived features are never edited directly - this single control owns them. */
export const spHour = new Atom<number>(12);
/** SHAP decomposition of the current feature vector (null while pending). */
export const spExplain = new Atom<SPExplain | null>(null);
/** Predicted-vs-actual series over the distribution window (null until loaded). */
export const spTimeseries = new Atom<SPTimeseries | null>(null);
export const spTimeseriesLoading = new Atom<boolean>(false);
/** SP correction factors in force across the window, for the timeline strip. */
export const spCorrectionsSeries = new Atom<SPCorrections | null>(null);
/** The feature currently swept on the dependence plot's x-axis (null until a
 * schema loads). Every other input is held at its slider value. */
export const spXFeature = new Atom<string | null>(null);
/** Dependence sweep: predicted SP over `spXFeature` + the conditioned scatter. */
export const spSweep = new Atom<SPSweep | null>(null);
export const spSweepLoading = new Atom<boolean>(false);

/* ─── Model Viewer selection ──────────────────────────────
 * The ships and window every Model Viewer model reads - Service Power and SFOC
 * alike, so switching between them keeps what you picked. Separate from
 * Explore's `selectedShip` / `startDate` / `endDate`: the sidebar's Query card
 * edits these through `modelSelection` (features/ship/model-selection.ts),
 * which starts them from Explore's the first time the Model Viewer opens.
 * Service Power is single-ship and reads the first (anchor) ship.
 */
export const modelShips = new Atom<string[]>([]);
/** The window (UTC datetime-local strings) and the preset that made it. */
const modelDefaultWindow = presetWindow('1mo')!;
export const modelStart = new Atom<string>(modelDefaultWindow.start);
export const modelEnd = new Atom<string>(modelDefaultWindow.end);
export const modelPreset = new Atom<DatePreset>('1mo');

/* ─── SFOC viewer ──────────────────────────────────────────
 * Curves are selected individually: `sfocSelected` holds opaque curve keys
 * (`ship‖engine‖fuel`, see features/sfoc/sfoc-selection.ts). `sfocData` caches
 * each picked ship's full curve set for the current `sfocDate`.
 */
/** How measured SFOC is drawn behind the curves (features/sfoc/sfoc-measured.ts). */
export type SfocMeasuredMode = 'off' | 'scatter' | 'heatmap' | 'load';
export const sfocMeasured = new Atom<SfocMeasuredMode>('scatter');
/** One chart for every ship's curves, or one panel per ship (same colours). */
export const sfocLayout = new Atom<'overlay' | 'grid'>('overlay');
/** Which tables sit under the SFOC chart. */
export const sfocTables = new Atom<{ values: boolean; formulas: boolean }>({
  values: true,
  formulas: true,
});
/** Date (YYYY-MM-DD, UTC) at which SFOC curves are looked up - the last day of
 * the window, never later than today (see `curveDateFor`). */
export const sfocDate = new Atom<string>(new Date().toISOString().slice(0, 10));
/** Fetched curves keyed by ship_id (the lazy per-ship cache). */
export const sfocData = new Atom<Record<string, SfocCurves>>({});
/** Selected curve keys (`ship‖engine‖fuel`). */
export const sfocSelected = new Atom<Set<string>>(new Set());
/** Ships whose matrix is expanded in the sidebar. */
export const sfocExpanded = new Atom<Set<string>>(new Set());
/** Ships whose curves are currently being fetched. */
export const sfocLoadingShips = new Atom<Set<string>>(new Set());
export const sfocError = new Atom<string | null>(null);

/** Convenience wrappers for the Set-typed atom. */
export const colSelection = {
  has: (name: string): boolean => selectedCols.value.has(name),
  toggle: (name: string): void => {
    const set = new Set(selectedCols.value);
    if (set.has(name)) set.delete(name);
    else set.add(name);
    selectedCols.set(set);
  },
  add: (names: Iterable<string>): void => {
    const set = new Set(selectedCols.value);
    for (const n of names) set.add(n);
    selectedCols.set(set);
  },
  remove: (names: Iterable<string>): void => {
    const set = new Set(selectedCols.value);
    for (const n of names) set.delete(n);
    selectedCols.set(set);
  },
  clear: (): void => selectedCols.set(new Set()),
  setAll: (names: Iterable<string>): void => selectedCols.set(new Set(names)),
};
